/** Sign in, then choose which books to work in.
 *
 * Two steps, because the ERP needs to establish three things and Cognito
 * only settles one of them (AUTH_DESIGN.md §3): who you are, which company,
 * and which financial year.
 *
 * The company step is skipped when there is nothing to decide — one company
 * with one open year needs no question asking. For everyone else it is a
 * deliberate choice, because posting a purchase order into the wrong entity
 * is not a mistake anybody notices quickly.
 */

import { useState } from 'react'
import { api, ApiError, setToken } from '@/lib/api'
import type { Session } from '@/types'

interface Company {
  company_id: string
  name: string
  years: { financial_year: string; status: 'Open' | 'Closed'; is_current: boolean }[]
}

/** Cognito answers a new account's first sign-in with a CHALLENGE rather
 *  than tokens: the person has to set their own password before anything
 *  else. `session` is Cognito's short-lived, single-use proof that they just
 *  presented the temporary password — it is not an ERP session and grants
 *  nothing. AUTH_DESIGN.md §6.1. */
interface Challenge {
  challenge: 'NEW_PASSWORD_REQUIRED' | string
  session: string
}

interface LoginResult {
  access_token: string
  user: { user_id: string; name: string; username: string }
  companies: Company[]
  session?: { company_id: string; financial_year: string; expires_at: number }
}

export function LoginScreen(
  { onSignedIn, notice }: { onSignedIn: (s: Session, token: string) => void
                            notice?: string | null },
) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [pending, setPending] = useState<LoginResult | null>(null)
  const [challenge, setChallenge] = useState<Challenge | null>(null)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [choice, setChoice] = useState<{ company: string; year: string }>(
    { company: '', year: '' })

  function toSession(r: LoginResult, companyId: string, year: string,
                     expiresAt: number): Session {
    const company = r.companies.find(c => c.company_id === companyId)!
    const y = company.years.find(v => v.financial_year === year)!
    return {
      userId: r.user.user_id, name: r.user.name,
      companyId, companyName: company.name,
      financialYear: year, yearStatus: y.status,
      availableCompanies: r.companies.map(c => ({ companyId: c.company_id, name: c.name })),
      availableYears: company.years.map(v => ({ financialYear: v.financial_year,
                                                status: v.status })),
      expiresAt,
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!username.trim() || !password) {
      setError('Username and password are required.'); return
    }
    setBusy(true)
    try {
      const r = await api.create<LoginResult & Partial<Challenge>>(
        '/auth/login', { username, password })

      // A new account. No tokens, nothing signed in — the person sets their
      // own password first, and no administrator ever knows what it is.
      //
      // ONLY this challenge. Cognito can ask several things — MFA, a
      // device confirmation — and treating them all as "set your password"
      // shows the wrong screen and then fails on submit. Anything else is
      // reported honestly instead of guessed at.
      if (r.challenge === 'NEW_PASSWORD_REQUIRED') {
        setChallenge({ challenge: r.challenge, session: r.session as string })
        return
      }
      if (r.challenge) {
        setError(`This account needs ${r.challenge} to sign in, which this ` +
                 `version cannot do yet. Contact your administrator.`)
        return
      }

      if (r.session) {
        // Nothing to decide — straight in.
        onSignedIn(toSession(r, r.session.company_id, r.session.financial_year,
                             r.session.expires_at), r.access_token)
        return
      }
      // A choice to make. No session exists yet, deliberately - but the
      // person IS authenticated, and the next call (/auth/session) has to
      // prove it. Without this the chooser posts with no Authorization
      // header, the server cannot tell who is asking, and the answer is a
      // 401 that reads as "your session has ended" - on the screen you reach
      // by signing in successfully.
      //
      // The single-company path never hit this: it calls onSignedIn(), which
      // sets the token as a side effect. So sign-in worked for exactly as
      // long as there was only one company to choose from.
      setToken(r.access_token)

      const first = r.companies[0]
      const openYear = first.years.find(y => y.is_current && y.status === 'Open')
                    ?? first.years.find(y => y.status === 'Open')
      setChoice({ company: first.company_id, year: openYear?.financial_year ?? '' })
      setPending(r)
    } catch (err) {
      setError(err instanceof ApiError ? err.message
                                       : 'Sign-in could not be completed.')
    } finally { setBusy(false) }
  }

  async function activate(e: React.FormEvent) {
    e.preventDefault()
    if (!challenge) return
    if (newPassword !== confirmPassword) {
      setError('The two passwords do not match.'); return
    }
    setBusy(true); setError(null)
    try {
      const r = await api.create<LoginResult>('/auth/activate', {
        username, session: challenge.session, new_password: newPassword,
      })
      setChallenge(null)
      // From here it is an ordinary sign-in: straight in if there is one
      // company and one open year, otherwise choose.
      if (r.session) {
        setToken(r.access_token)
        onSignedIn(toSession(r, r.session.company_id, r.session.financial_year,
                             r.session.expires_at), r.access_token)
        return
      }
      setToken(r.access_token)
      const first = r.companies[0]
      const openYear = first.years.find(y => y.is_current && y.status === 'Open')
                    ?? first.years.find(y => y.status === 'Open')
      setChoice({ company: first.company_id, year: openYear?.financial_year ?? '' })
      setPending(r)
    } catch (err) {
      setError(err instanceof ApiError ? err.message
                                       : 'The password could not be set.')
    } finally { setBusy(false) }
  }

  async function begin(e: React.FormEvent) {
    e.preventDefault()
    if (!pending) return
    setBusy(true); setError(null)
    try {
      const r = await api.create<{ session: { expires_at: number } }>(
        '/auth/session',
        { company_id: choice.company, financial_year: choice.year })
      onSignedIn(toSession(pending, choice.company, choice.year,
                           r.session.expires_at), pending.access_token)
    } catch (err) {
      setError(err instanceof ApiError ? err.message
                                       : 'The session could not be started.')
    } finally { setBusy(false) }
  }

  const company = pending?.companies.find(c => c.company_id === choice.company)
  const chosenYear = company?.years.find(y => y.financial_year === choice.year)

  return (
    <div className="login-page">
      <form className="login-card" onSubmit={challenge ? activate : pending ? begin : submit}>
        <div className="login-brand">
          {/* Navy variant on the light card, sized by width (not a fixed
              height) so it never overflows a narrow card — UI_TASTE.md. */}
          <img src="/fkmt-logo-v2.png" alt="FKMT" className="login-logo" />
          <div className="login-app">ERP</div>
        </div>

        {/* Never a blank login screen — always say why they are here.
            UI_TASTE.md.

            One message at a time. A 401 sets BOTH the sign-out notice and an
            error, so rendering the two unconditionally showed the identical
            sentence twice in two different colours. The error is the newer
            and more specific of the two, so it wins.

            The notice is also dropped once `pending` is set: by then the
            person has authenticated and is choosing their books, and
            "your session has ended" is no longer true of them. */}
        {!error && !pending && !challenge && notice &&
          <div className="login-notice">{notice}</div>}
        {error && <div className="login-error" role="alert">{error}</div>}

        {challenge ? (
          <>
            {/* A new account's first use. Cognito issued a temporary password
                and holds the account until the person replaces it — so the
                one who created the account never learns what it becomes.
                AUTH_DESIGN.md §6.1. */}
            <p className="login-welcome">
              Welcome. Choose a password for your account before you continue.
            </p>

            <div className="login-field">
              <label htmlFor="np">
                New Password <span className="hint">min 6 chars, with A-Z, a-z and a symbol</span>
              </label>
              <input id="np" type="password" value={newPassword} autoFocus
                     autoComplete="new-password"
                     onChange={e => setNewPassword(e.target.value)} />
            </div>
            <div className="login-field">
              <label htmlFor="cp">Confirm Password</label>
              <input id="cp" type="password" value={confirmPassword}
                     autoComplete="new-password"
                     onChange={e => setConfirmPassword(e.target.value)} />
            </div>
            <button className="btn btn-login" disabled={busy}>
              {busy ? 'Setting password…' : 'Set password and continue'}
            </button>
          </>
        ) : !pending ? (
          <>
            <div className="login-field">
              <label htmlFor="u">Username</label>
              <input id="u" value={username} autoComplete="username" autoFocus
                     onChange={e => setUsername(e.target.value)} />
            </div>
            <div className="login-field">
              <label htmlFor="p">
                Password <span className="hint">min 6 chars, with A-Z, a-z and a symbol</span>
              </label>
              <input id="p" type="password" value={password}
                     autoComplete="current-password"
                     onChange={e => setPassword(e.target.value)} />
            </div>
            <button className="btn btn-login" disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </>
        ) : (
          <>
            <p className="login-welcome">
              Welcome, {pending.user.name}. Which books are you working in?
            </p>

            <div className="login-field">
              <label htmlFor="c">Company</label>
              <select id="c" value={choice.company} autoFocus
                      onChange={e => {
                        const c = pending.companies.find(x => x.company_id === e.target.value)!
                        const y = c.years.find(v => v.is_current && v.status === 'Open')
                               ?? c.years.find(v => v.status === 'Open')
                               ?? c.years[0]
                        setChoice({ company: c.company_id, year: y.financial_year })
                      }}>
                {pending.companies.map(c => (
                  <option key={c.company_id} value={c.company_id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="login-field">
              <label htmlFor="y">Financial Year</label>
              <select id="y" value={choice.year}
                      onChange={e => setChoice(s => ({ ...s, year: e.target.value }))}>
                {(company?.years ?? []).map(y => (
                  <option key={y.financial_year} value={y.financial_year}>
                    {y.financial_year}{y.status === 'Closed' ? ' — closed' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Said before they are in, not after they try to save. */}
            {chosenYear?.status === 'Closed' && (
              <div className="login-notice">
                {choice.year} is closed. You will be able to read and print
                history, but not create or change records.
              </div>
            )}

            <button className="btn btn-login" disabled={busy || !choice.year}>
              {busy ? 'Opening…' : 'Continue'}
            </button>
            <button type="button" className="link-btn login-back"
                    onClick={() => { setPending(null); setPassword('') }}>
              ← Sign in as someone else
            </button>
          </>
        )}
      </form>
    </div>
  )
}
